import React from 'react';
import { Search, X, Package, Archive, Plus, Clock3, CheckCircle2, AlertCircle, ExternalLink, RefreshCw, ChevronDown } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = data?.error || `Request failed: ${res.status}`;
    throw new Error(message);
  }
  return data;
};

const statusMeta = {
  processing: { label: 'В обработке', className: 'bg-orange-400/15 text-orange-300 border-orange-400/35' },
  tokyo_arrived: { label: 'Поступил в Токио', className: 'bg-[#8A5CF6]/15 text-accent border-[#8A5CF6]/35' },
  in_transit: { label: 'В Пути', className: 'bg-neon-blue/15 text-neon-blue border-neon-blue/30' },
  ready_pickup: { label: 'Готов к выдаче', className: 'bg-neon-yellow/15 text-neon-yellow border-neon-yellow/35' },
  received: { label: 'Получен', className: 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30' },
};

const STATUS_LABEL_TO_KEY = {
  'В обработке': 'processing',
  'Поступил в Токио': 'tokyo_arrived',
  'Прибыл в Токио': 'tokyo_arrived',
  'Прибыл на склад в Японии': 'tokyo_arrived',
  'В пути': 'in_transit',
  'В Пути': 'in_transit',
  'Готов к выдаче': 'ready_pickup',
  'Получен': 'received',
};

const URL_STATUS_FILTERS = new Set(['all', 'processing', 'tokyo_arrived', 'in_transit', 'ready_pickup', 'paid_ready', 'received']);

const normalizeStatus = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return 'in_transit';
  if (statusMeta[s]) return s;
  if (STATUS_LABEL_TO_KEY[s]) return STATUS_LABEL_TO_KEY[s];
  return 'in_transit';
};

const mapApiOrder = (o) => {
  const track = String(o?.track_number || '').trim();
  return {
    id: String(o?.id || track),
    track,
    fullName: o?.full_name || '',
    status: normalizeStatus(o?.status),
    quantity: o?.quantity,
    weight: o?.weight,
    usd: o?.usd,
    som: o?.som,
    tenge: o?.tenge,
    paymentStatus: o?.payment_status || '',
    paymentMethod: o?.payment_method || '',
    pickupCode: o?.pickup_code || '',
    qrData: o?.qr_data || null,
    paidAt: o?.paid_at || '',
    country: o?.country || 'KG',
    source: o?.source || 'excel',
    manualProcessed: !!o?.manual_processed,
  };
};

const formatNumber = (value, { decimals = 2 } = {}) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return '—';
  const fixed = n.toFixed(decimals);
  const trimmed = fixed.replace(/\.00$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
  const [intPart, decPart] = trimmed.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return decPart ? `${grouped}.${decPart}` : grouped;
};

const formatDateShort = (value) => {
  if (!value) return '';
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  const date = new Date(text);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleDateString('ru-RU');
  }
  return text;
};

const formatDateTimeBishkek = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Asia/Bishkek',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date);
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const paymentStatusLabel = (status) => {
  const value = String(status || '').trim();
  if (value === 'PROCESSED' || value === 'Оплачено') return 'Оплачено';
  if (value === 'ERROR') return 'Ошибка оплаты';
  if (value === 'NOT_EXIST') return 'Оплата не найдена';
  return 'Ожидает оплаты';
};

const PaymentStatusBadge = ({ status }) => {
  const label = paymentStatusLabel(status);
  if (label === 'Оплачено') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/35 bg-emerald-400/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">
        <CheckCircle2 size={13} />
        {label}
      </span>
    );
  }
  if (label === 'Ошибка оплаты' || label === 'Оплата не найдена') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-400/35 bg-red-400/15 px-2.5 py-1 text-[11px] font-semibold text-red-200">
        <AlertCircle size={13} />
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-neon-yellow/40 bg-neon-yellow/15 px-2.5 py-1 text-[11px] font-semibold text-neon-yellow shadow-[0_0_18px_rgba(250,204,21,0.12)]">
      <Clock3 size={13} />
      {label}
    </span>
  );
};

const InlineLoader = ({ label }) => {
  return (
    <div className="h-5 flex items-center gap-2 text-xs text-text-secondary">
      <span className="inline-block h-4 w-4 rounded-full border-2 border-white/15 border-t-white/60 animate-spin" />
      {label ? <span>{label}</span> : null}
    </div>
  );
};

const ScreenLoader = ({ show }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 backdrop-blur-sm">
      <div className="rounded-2xl border border-white/10 bg-[#0F0F0F]/90 px-5 py-4 shadow-[0_18px_70px_rgba(0,0,0,0.55)]">
        <InlineLoader label="Загрузка данных" />
      </div>
    </div>
  );
};

const PaymentStatusModal = ({ open, title, message, amount, orders, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#111111] p-5 shadow-[0_18px_70px_rgba(0,0,0,0.55)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-bold text-text-primary">{title}</div>
            {amount ? (
              <div className="mt-3 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-emerald-100/70">Сумма оплаты</div>
                <div className="mt-0.5 text-lg font-bold text-emerald-50">{amount}</div>
              </div>
            ) : null}
            {Array.isArray(orders) && orders.length ? (
              <div className="mt-3 space-y-2">
                <div className="text-[11px] uppercase tracking-wide text-text-muted">
                  {orders.length === 1 ? 'Код выдачи' : `Коды выдачи · ${orders.length} заказа`}
                </div>
                {orders.map((order) => (
                  <div key={order.id || order.track} className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2">
                    <div className="font-mono text-xs text-text-secondary break-all">{order.track}</div>
                    <div className="mt-1 font-mono text-xl font-bold text-text-primary">{order.pickupCode || 'не назначен'}</div>
                  </div>
                ))}
              </div>
            ) : null}
            <div className="mt-3 text-sm leading-relaxed text-text-secondary">{message}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-stroke bg-white/5 text-text-secondary"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-5 h-11 w-full rounded-2xl bg-neon-yellow text-sm font-bold text-black"
        >
          Понятно
        </button>
      </div>
    </div>
  );
};

const getDemoOrders = () => {
  return [
    {
      id: 'demo-1',
      track: 'JPX-012345',
      fullName: 'Иван Иванов',
      status: 'tokyo_arrived',
      quantity: 2,
      weight: 1.4,
      usd: 35,
      som: 3100,
      history: [
        { status: 'Поступил в Токио', status_date: '2026-02-01' },
      ],
    },
    {
      id: 'demo-2',
      track: 'JPX-067890',
      fullName: 'Алина Ким',
      status: 'in_transit',
      quantity: 1,
      weight: 0.8,
      usd: 18,
      som: 1600,
      history: [
        { status: 'Поступил в Токио', status_date: '2026-02-03' },
        { status: 'В Пути', status_date: '2026-02-05' },
      ],
    },
    {
      id: 'demo-3',
      track: 'JPX-112233',
      fullName: 'Нурбек Садыков',
      status: 'ready_pickup',
      quantity: 3,
      weight: 2.15,
      usd: 52,
      som: 4600,
      history: [
        { status: 'Поступил в Токио', status_date: '2026-01-28' },
        { status: 'В Пути', status_date: '2026-01-30' },
        { status: 'Готов к выдаче', status_date: '2026-02-02' },
      ],
    },
    {
      id: 'demo-4',
      track: 'JPX-998877',
      fullName: 'Данияр Абдрахманов',
      status: 'received',
      quantity: 1,
      weight: 0.55,
      usd: 12,
      som: 980,
      history: [
        { status: 'Поступил в Токио', status_date: '2026-01-10' },
        { status: 'В Пути', status_date: '2026-01-12' },
        { status: 'Готов к выдаче', status_date: '2026-01-16' },
        { status: 'Получен', status_date: '2026-01-18' },
      ],
    },
  ];
};

const StatusBadge = ({ status }) => {
  const meta = statusMeta[status] || statusMeta.in_transit;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${meta.className}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
      {meta.label}
    </span>
  );
};

const FilterTile = ({ title, value, colorClassName, active, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border backdrop-blur-md px-2.5 py-2 text-left transition w-full ${
        active
          ? 'border-white/25 bg-white/8'
          : 'border-stroke bg-surface hover:bg-surface-hover'
      }`}
      style={
        active
          ? {
              boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 16px 55px rgba(59,130,246,0.10)',
            }
          : undefined
      }
    >
      <div className="truncate text-[10px] leading-tight text-text-muted">{title}</div>
      <div className={`mt-0.5 text-sm font-semibold leading-tight ${colorClassName}`}>{value}</div>
    </button>
  );
};

const OrderCard = ({ order, userCountry }) => {
  const { initData } = useTelegram();
  const [open, setOpen] = React.useState(false);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [history, setHistory] = React.useState(Array.isArray(order?.history) ? order.history : []);

  const currencyLabel = (order.country === 'KZ') ? 'Тенге' : 'Сом';
  const currencyValue = (order.country === 'KZ') ? order.tenge : order.som;
  const payLabel = paymentStatusLabel(order.paymentStatus);
  const isPaidReadyPickup = order.status === 'ready_pickup' && payLabel === 'Оплачено';

  const loadHistory = async () => {
    setError('');

    if (Array.isArray(order?.history) && order.history.length > 0) {
      setHistory(order.history);
      return;
    }

    const initDataRaw = initData?.initData;
    if (!initDataRaw) {
      setError('Telegram initData ещё не готов');
      return;
    }

    setLoading(true);
    try {
      const r = await apiPost('/api/orders/history', { initData: initDataRaw, trackNumber: order.track });
      setHistory(Array.isArray(r?.history) ? r.history : []);
    } catch (e) {
      setError(e?.message || 'Ошибка загрузки истории');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-2xl border backdrop-blur-md p-4 ${
      isPaidReadyPickup
        ? 'border-emerald-400/45 bg-emerald-400/10 shadow-[0_0_0_1px_rgba(52,211,153,0.12),0_18px_70px_rgba(16,185,129,0.12)]'
        : 'border-stroke bg-surface'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          {isPaidReadyPickup ? (
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-400/35 bg-emerald-400/15 px-2.5 py-1 text-[11px] font-bold text-emerald-100">
              <CheckCircle2 size={13} />
              Можно получать
            </div>
          ) : null}
          <div className="text-sm font-semibold text-text-primary">{order.track}</div>
          {order.fullName ? <div className="mt-1 text-xs text-text-muted">{order.fullName}</div> : null}
        </div>
        <StatusBadge status={order.status} />
      </div>

      {order.status === 'ready_pickup' ? (
        <div className={`mt-3 rounded-xl border px-3 py-2 text-xs ${
          payLabel === 'Оплачено'
            ? 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200'
            : 'border-neon-yellow/20 bg-neon-yellow/10 text-neon-yellow'
        }`}>
          <div className="font-semibold">{payLabel}</div>
          <div className={`mt-2 rounded-xl border px-3 py-3 ${
            payLabel === 'Оплачено'
              ? 'border-emerald-400/35 bg-emerald-400/10'
              : 'border-neon-yellow/30 bg-black/10'
          }`}>
            <div className={`text-[11px] uppercase tracking-wide ${
              payLabel === 'Оплачено' ? 'text-emerald-100/70' : 'text-neon-yellow/80'
            }`}>
              Код выдачи
            </div>
            <div className={`mt-0.5 font-mono text-2xl font-black tracking-wide ${
              payLabel === 'Оплачено' ? 'text-emerald-50' : 'text-neon-yellow'
            }`}>
              {order.pickupCode || 'не назначен'}
            </div>
            <div className={`mt-1 text-[11px] ${
              payLabel === 'Оплачено' ? 'text-emerald-100/70' : 'text-text-secondary'
            }`}>
              Этот код можно ввести в пункте выдачи.
            </div>
          </div>
          {payLabel === 'Оплачено' ? (
            <>
              <div className="mt-1 text-emerald-100/80">
                {order.paidAt ? `Оплачено: ${formatDateTimeBishkek(order.paidAt)}` : 'Оплата подтверждена'}
              </div>
            </>
          ) : (
            <div className="mt-1 text-text-secondary">Можно оплатить онлайн через QR.</div>
          )}
        </div>
      ) : null}

      <div className="mt-3 grid grid-cols-1 gap-2">
        <button
          type="button"
          onClick={() => setDetailsOpen((v) => !v)}
          className="rounded-xl border border-stroke bg-white/3 px-3 py-2 text-left hover:bg-white/5 transition"
        >
          <div className="text-[11px] uppercase tracking-wide text-text-muted">Трек</div>
          <div className="mt-1 font-mono text-[12px] text-text-primary/90 break-all">{order.track}</div>
          <div className="mt-1 text-[11px] text-text-secondary">{detailsOpen ? 'Скрыть детали' : 'Показать детали'}</div>
        </button>

        {detailsOpen ? (
          <div className="rounded-xl border border-stroke bg-white/3 px-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-muted">Количество</div>
                <div className="mt-0.5 text-sm text-text-primary">{order.quantity ?? '—'}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-muted">Вес</div>
                <div className="mt-0.5 text-sm text-text-primary">{formatNumber(order.weight, { decimals: 2 })}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-muted">Доллар</div>
                <div className="mt-0.5 text-sm text-text-primary">{formatNumber(order.usd, { decimals: 2 })}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-text-muted">{currencyLabel}</div>
                <div className="mt-0.5 text-sm text-text-primary">{formatNumber(currencyValue, { decimals: 2 })}</div>
              </div>
            </div>
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => {
            const next = !open;
            setOpen(next);
            if (next && history.length === 0 && !loading) loadHistory();
          }}
          className="rounded-xl border border-stroke bg-white/3 px-3 py-2 text-left hover:bg-white/5 transition"
        >
          <div className="text-[11px] uppercase tracking-wide text-text-muted">История статусов</div>
          <div className="mt-1 text-xs text-text-secondary">{open ? 'Скрыть' : 'Показать'}</div>
        </button>

        {open ? (
          <div className="rounded-xl border border-stroke bg-white/3 px-3 py-3">
            {loading ? <InlineLoader label="Загрузка" /> : null}
            {error ? <div className="text-xs text-red-300">{error}</div> : null}
            {!loading && !error && history.length === 0 ? (
              <div className="text-xs text-text-secondary">История пока отсутствует</div>
            ) : null}

            {!loading && !error && history.length > 0 ? (
              <div className="space-y-2">
                {history.map((h, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="mt-1 h-2 w-2 rounded-full bg-white/35" />
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-text-primary">{String(h?.status || '')}</div>
                      <div className="mt-0.5 text-[11px] text-text-secondary">
                        {formatDateShort(h?.status_date || h?.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const OrdersPage = ({ userCountry }) => {
  const { initData } = useTelegram();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [orders, setOrders] = React.useState([]);
  const [manualTrack, setManualTrack] = React.useState('');
  const [manualTrackLoading, setManualTrackLoading] = React.useState(false);
  const [manualTrackMessage, setManualTrackMessage] = React.useState('');
  const [paymentLoading, setPaymentLoading] = React.useState(false);
  const [paymentStatusLoading, setPaymentStatusLoading] = React.useState(false);
  const [paymentError, setPaymentError] = React.useState('');
  const [paymentMessage, setPaymentMessage] = React.useState('');
  const [paymentQr, setPaymentQr] = React.useState(null);
  const [paymentStatusModal, setPaymentStatusModal] = React.useState(null);
  const [paymentNow, setPaymentNow] = React.useState(() => Date.now());
  const [paidOrdersExpanded, setPaidOrdersExpanded] = React.useState(false);
  const autoPaymentNotifiedRef = React.useRef('');

  const data = React.useMemo(() => orders, [orders]);

  const [tab, setTab] = React.useState('current');
  const [trackQuery, setTrackQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const autoReadyFilterAppliedRef = React.useRef(false);

  const selectStatusFilter = React.useCallback((nextStatus) => {
    autoReadyFilterAppliedRef.current = true;
    setStatusFilter(nextStatus);
  }, []);

  React.useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const track = String(q.get('track') || '').trim();
      const status = String(q.get('status') || '').trim();
      if (track) {
        setTrackQuery(track);
      }
      if (URL_STATUS_FILTERS.has(status)) {
        autoReadyFilterAppliedRef.current = true;
        setTab('current');
        setStatusFilter(status);
      }
    } catch (_) {
      // ignore
    }
  }, []);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      const initDataRaw = initData?.initData;
      if (!initDataRaw) return;
      setError('');
      setLoading(true);
      try {
        const r = await apiPost('/api/orders/list', { initData: initDataRaw });
        if (!alive) return;
        const list = Array.isArray(r?.orders) ? r.orders.map(mapApiOrder) : [];
        setOrders(list);
      } catch (e) {
        if (!alive) return;
        setError(e?.message || 'Ошибка загрузки заказов');
        setOrders([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, [initData?.initData]);

  const addManualTrack = async () => {
    const initDataRaw = initData?.initData;
    const trackNumber = manualTrack.trim();
    if (!initDataRaw || !trackNumber) return;
    if (trackNumber.length < 5) {
      setManualTrackMessage('Трек-номер должен содержать минимум 5 символов');
      return;
    }
    setManualTrackLoading(true);
    setManualTrackMessage('');
    try {
      await apiPost('/api/orders/manual/add', { initData: initDataRaw, trackNumber });
      setManualTrack('');
      setManualTrackMessage('Трек добавлен и ожидает обработки менеджером');
      const result = await apiPost('/api/orders/list', { initData: initDataRaw });
      setOrders(Array.isArray(result?.orders) ? result.orders.map(mapApiOrder) : []);
    } catch (e) {
      setManualTrackMessage(e?.message || 'Не удалось добавить трек');
    } finally {
      setManualTrackLoading(false);
    }
  };

  const summary = React.useMemo(() => {
    const base = { processing: 0, tokyo_arrived: 0, in_transit: 0, ready_pickup: 0, paid_ready: 0, received: 0 };
    for (const o of data) {
      if (o.status && Object.prototype.hasOwnProperty.call(base, o.status)) base[o.status] += 1;
      if (o.status === 'ready_pickup' && paymentStatusLabel(o.paymentStatus) === 'Оплачено') base.paid_ready += 1;
    }
    return base;
  }, [data]);

  React.useEffect(() => {
    if (autoReadyFilterAppliedRef.current) return;
    if (tab !== 'current' || statusFilter !== 'all' || trackQuery) return;
    if (summary.ready_pickup > 0) {
      autoReadyFilterAppliedRef.current = true;
      setStatusFilter('ready_pickup');
    }
  }, [summary.ready_pickup, statusFilter, tab, trackQuery]);

  const filteredData = React.useMemo(() => {
    const q = (trackQuery || '').trim().toLowerCase();
    return data.filter((o) => {
      const tabOk = tab === 'received' ? o.status === 'received' : o.status !== 'received';
      const statusOk = tab !== 'current'
        ? true
        : statusFilter === 'all'
          ? true
          : statusFilter === 'paid_ready'
            ? o.status === 'ready_pickup' && paymentStatusLabel(o.paymentStatus) === 'Оплачено'
            : o.status === statusFilter;
      const trackOk = !q ? true : String(o.track || '').toLowerCase().includes(q);
      return tabOk && statusOk && trackOk;
    });
  }, [data, tab, trackQuery, statusFilter]);

  const readyPickupTotals = React.useMemo(() => {
    if (tab !== 'current' || statusFilter !== 'ready_pickup') {
      return {
        count: 0,
        unpaidCount: 0,
        paidCount: 0,
        totalWeight: 0,
        totalSom: 0,
        totalTenge: 0,
        unpaidSom: 0,
        unpaidTenge: 0,
        paidSom: 0,
        paidTenge: 0,
      };
    }

    const ready = filteredData;
    const unpaid = ready.filter((o) => paymentStatusLabel(o.paymentStatus) !== 'Оплачено');
    const paid = ready.filter((o) => paymentStatusLabel(o.paymentStatus) === 'Оплачено');
    let totalWeight = 0;
    let totalSom = 0;
    let totalTenge = 0;
    let unpaidSom = 0;
    let unpaidTenge = 0;
    let paidSom = 0;
    let paidTenge = 0;
    for (const o of ready) {
      totalWeight += toFiniteNumber(o?.weight);
      totalSom += toFiniteNumber(o?.som);
      totalTenge += toFiniteNumber(o?.tenge);
      if (paymentStatusLabel(o.paymentStatus) === 'Оплачено') {
        paidSom += toFiniteNumber(o?.som);
        paidTenge += toFiniteNumber(o?.tenge);
      } else {
        unpaidSom += toFiniteNumber(o?.som);
        unpaidTenge += toFiniteNumber(o?.tenge);
      }
    }

    return {
      count: ready.length,
      unpaidCount: unpaid.length,
      paidCount: paid.length,
      totalWeight,
      totalSom,
      totalTenge,
      unpaidSom,
      unpaidTenge,
      paidSom,
      paidTenge,
    };
  }, [filteredData, tab, statusFilter]);

  const readyPaymentSummary = React.useMemo(() => {
    const ready = filteredData.filter((o) => o.status === 'ready_pickup');
    const unpaid = ready.filter((o) => paymentStatusLabel(o.paymentStatus) !== 'Оплачено');
    const paid = ready.filter((o) => paymentStatusLabel(o.paymentStatus) === 'Оплачено');
    return {
      ready,
      unpaid,
      paid,
      allPaid: ready.length > 0 && unpaid.length === 0,
    };
  }, [filteredData]);
  const paidOrdersPreviewLimit = 3;
  const visiblePaidOrders = paidOrdersExpanded
    ? readyPaymentSummary.paid
    : readyPaymentSummary.paid.slice(0, paidOrdersPreviewLimit);
  const hiddenPaidOrdersCount = Math.max(0, readyPaymentSummary.paid.length - visiblePaidOrders.length);
  const onlinePaymentAvailable = userCountry === 'KG';
  const paymentQrExpiresAt = paymentQr?.qr?.expiresAt ? new Date(paymentQr.qr.expiresAt).getTime() : null;
  const paymentQrExpired = !!paymentQrExpiresAt && paymentQrExpiresAt <= paymentNow;

  React.useEffect(() => {
    if (!paymentQr?.qr?.expiresAt) return undefined;
    const timer = window.setInterval(() => setPaymentNow(Date.now()), 15000);
    return () => window.clearInterval(timer);
  }, [paymentQr?.qr?.expiresAt]);

  React.useEffect(() => {
    const initDataRaw = initData?.initData;
    const transactionId = paymentQr?.qr?.transactionId;
    if (!onlinePaymentAvailable || !initDataRaw || !transactionId || paymentQrExpired || readyPaymentSummary.allPaid) {
      return undefined;
    }
    if (paymentStatusLabel(paymentQr?.payment?.status) === 'Оплачено') {
      return undefined;
    }

    const checkPayment = async () => {
      try {
        const r = await apiPost('/api/payments/status', { initData: initDataRaw, transactionId });
        const nextPayment = r?.effectivePayment || r?.payment || paymentQr?.payment;
        const label = paymentStatusLabel(nextPayment?.status);
        setPaymentQr((prev) => ({ ...(prev || {}), payment: nextPayment, orders: r?.orders || prev?.orders || [] }));
        if (label === 'Оплачено') {
          const nextOrders = await reloadOrders();
          setPaymentQr(null);
          setPaymentMessage('');
          if (autoPaymentNotifiedRef.current !== transactionId) {
            autoPaymentNotifiedRef.current = transactionId;
            setPaymentStatusModal(buildPaymentSuccessModal(nextPayment, nextOrders, r?.orders || []));
          }
        }
      } catch (_) {
        // Автопроверка не должна мешать пользователю. Ошибку покажет ручная кнопка.
      }
    };

    const timer = window.setInterval(checkPayment, 10000);
    return () => window.clearInterval(timer);
  }, [
    initData?.initData,
    onlinePaymentAvailable,
    paymentQr?.qr?.transactionId,
    paymentQr?.payment?.status,
    paymentQrExpired,
    readyPaymentSummary.allPaid,
  ]);

  const reloadOrders = async () => {
    const initDataRaw = initData?.initData;
    if (!initDataRaw) return;
    const result = await apiPost('/api/orders/list', { initData: initDataRaw });
    const nextOrders = Array.isArray(result?.orders) ? result.orders.map(mapApiOrder) : [];
    setOrders(nextOrders);
    return nextOrders;
  };

  const buildPaymentSuccessModal = (payment, nextOrders, sourceOrders = paymentQr?.orders || []) => {
    const paidOrderIds = new Set(
      (sourceOrders || [])
        .map((order) => String(order.id || ''))
        .filter(Boolean)
    );
    const paidOrders = (nextOrders || orders)
      .filter((order) => paidOrderIds.size ? paidOrderIds.has(String(order.id)) : order.status === 'ready_pickup')
      .filter((order) => paymentStatusLabel(order.paymentStatus) === 'Оплачено');
    const amount = payment?.amount_som
      ? `${formatNumber(payment.amount_som, { decimals: 2 })} сом`
      : '';
    return {
      title: 'Оплата подтверждена',
      amount,
      orders: paidOrders,
      message: paidOrders.length
        ? 'Покажите код кассиру, чтобы забрать заказ.'
        : 'Покажите код выдачи кассиру, чтобы забрать заказ.',
    };
  };

  const createPaymentQr = async () => {
    const initDataRaw = initData?.initData;
    if (!initDataRaw) {
      setPaymentError('Telegram initData ещё не готов');
      return;
    }

    const orderIds = readyPaymentSummary.unpaid
      .map((o) => Number(o.id))
      .filter((id) => Number.isFinite(id));

    if (!orderIds.length) {
      setPaymentError('Нет заказов для оплаты');
      return;
    }

    setPaymentLoading(true);
    setPaymentError('');
    setPaymentMessage('');
    autoPaymentNotifiedRef.current = '';
    try {
      const r = await apiPost('/api/payments/optima/qr/create', { initData: initDataRaw, orderIds });
      setPaymentQr(r || null);
      setPaymentMessage(
        r?.overlap
          ? 'У вас уже есть активный счёт на часть заказов. Сначала оплатите его или дождитесь истечения QR, затем можно будет оплатить остальные заказы.'
          : r?.reused
            ? 'Показываем уже созданный QR. Он ещё действует.'
            : 'QR сформирован. Следуйте инструкции ниже.'
      );
      await reloadOrders();
    } catch (e) {
      setPaymentError(e?.message || 'Не удалось создать QR');
    } finally {
      setPaymentLoading(false);
    }
  };

  const refreshPaymentStatus = async () => {
    const initDataRaw = initData?.initData;
    const transactionId = paymentQr?.qr?.transactionId;
    if (!initDataRaw || !transactionId) return;

    setPaymentStatusLoading(true);
    setPaymentError('');
    setPaymentMessage('');
    try {
      const r = await apiPost('/api/payments/status', { initData: initDataRaw, transactionId });
      const nextPayment = r?.effectivePayment || r?.payment || paymentQr?.payment;
      const label = paymentStatusLabel(nextPayment?.status);
      setPaymentQr((prev) => ({ ...(prev || {}), payment: nextPayment, orders: r?.orders || prev?.orders || [] }));
      const nextOrders = await reloadOrders();
      if (label === 'Оплачено') {
        setPaymentQr(null);
        setPaymentMessage('');
      }
      setPaymentStatusModal(
        label === 'Оплачено'
          ? buildPaymentSuccessModal(nextPayment, nextOrders, r?.orders || [])
          : {
              title: 'Оплата пока не поступила',
              message: 'Если вы уже оплатили, подождите несколько секунд и проверьте статус ещё раз.',
            }
      );
    } catch (e) {
      setPaymentError(e?.message || 'Не удалось проверить оплату');
    } finally {
      setPaymentStatusLoading(false);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: 'track',
        header: 'Трек',
        cell: (info) => <span className="font-mono text-[12px] text-text-primary/90">{info.getValue()}</span>,
      },
      {
        accessorKey: 'fullName',
        header: 'ФИО',
        cell: (info) => <span className="text-text-secondary">{info.getValue()}</span>,
      },
      {
        accessorKey: 'status',
        header: 'Статус',
        cell: (info) => <StatusBadge status={info.getValue()} />,
      },
      {
        accessorKey: 'paymentStatus',
        header: 'Оплата',
        cell: (info) => <span className="text-text-secondary">{paymentStatusLabel(info.getValue())}</span>,
      },
    ],
    []
  );

  const [sorting, setSorting] = React.useState([]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="min-h-screen bg-bg text-text-primary pb-20">
      <ScreenLoader show={loading} />
      <PaymentStatusModal
        open={!!paymentStatusModal}
        title={paymentStatusModal?.title}
        message={paymentStatusModal?.message}
        amount={paymentStatusModal?.amount}
        orders={paymentStatusModal?.orders}
        onClose={() => setPaymentStatusModal(null)}
      />
      <div className="px-4 pt-6 max-w-5xl mx-auto">
        <h1 className="text-xl font-semibold tracking-tight">Мои заказы</h1>
        <p className="mt-2 text-sm text-text-secondary">История отправлений, статусы и трек-номера.</p>

        <div className="mt-5 rounded-2xl border border-stroke bg-surface p-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Plus size={17} className="text-neon-yellow" /> Добавить трек
          </div>
          <p className="mt-1 text-xs text-text-secondary">
            Трек появится со статусом «В обработке». Мы обновим его после проверки.
          </p>
          <div className="mt-3 flex gap-2">
            <input
              value={manualTrack}
              onChange={(e) => setManualTrack(e.target.value)}
              placeholder="Введите трек-номер"
              className="min-w-0 flex-1 rounded-2xl border border-stroke bg-[#161616] px-4 py-3 text-[16px] outline-none focus:border-white/25"
            />
            <button
              type="button"
              disabled={manualTrackLoading || manualTrack.trim().length < 5}
              onClick={addManualTrack}
              className="px-4 rounded-2xl bg-neon-yellow text-black text-sm font-semibold disabled:opacity-50"
            >
              {manualTrackLoading ? '...' : 'Добавить'}
            </button>
          </div>
          {manualTrackMessage ? <div className="mt-2 text-xs text-text-secondary">{manualTrackMessage}</div> : null}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setTab('current')}
            className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
              tab === 'current' ? 'border-white/25 bg-white/8' : 'border-stroke bg-surface hover:bg-surface-hover'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Package size={16} className={tab === 'current' ? 'text-neon-yellow' : 'text-text-muted'} />
              Текущие
            </div>
          </button>
          <button
            type="button"
            onClick={() => setTab('received')}
            className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
              tab === 'received' ? 'border-white/25 bg-white/8' : 'border-stroke bg-surface hover:bg-surface-hover'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Archive size={16} className={tab === 'received' ? 'text-neon-yellow' : 'text-text-muted'} />
              Полученные
            </div>
          </button>
        </div>

        <div className="mt-4">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              value={trackQuery}
              onChange={(e) => setTrackQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  try {
                    e.currentTarget.blur();
                  } catch (_) {
                    // ignore
                  }
                }
              }}
              placeholder="Введите номер трека"
              className="w-full rounded-2xl border border-stroke bg-surface pl-11 pr-11 py-3 text-[16px] text-text-primary placeholder:text-text-muted/70 outline-none focus:border-white/25"
            />
            {trackQuery ? (
              <button
                type="button"
                onClick={() => setTrackQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-xl border border-stroke bg-white/5 flex items-center justify-center hover:bg-white/8 transition"
              >
                <X size={14} className="text-text-muted" />
              </button>
            ) : null}
          </div>
        </div>

        {error ? <div className="mt-4 text-sm text-red-300">{error}</div> : null}

        {tab === 'current' ? (
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-7 gap-2">
            <FilterTile
              title="Все"
              value={summary.processing + summary.tokyo_arrived + summary.in_transit + summary.ready_pickup}
              colorClassName="text-text-primary"
              active={statusFilter === 'all'}
              onClick={() => selectStatusFilter('all')}
            />
            <FilterTile
              title="В обработке"
              value={summary.processing}
              colorClassName="text-orange-300"
              active={statusFilter === 'processing'}
              onClick={() => selectStatusFilter('processing')}
            />
            <FilterTile
              title="Поступил в Токио"
              value={summary.tokyo_arrived}
              colorClassName="text-accent"
              active={statusFilter === 'tokyo_arrived'}
              onClick={() => selectStatusFilter('tokyo_arrived')}
            />
            <FilterTile
              title="В Пути"
              value={summary.in_transit}
              colorClassName="text-neon-blue"
              active={statusFilter === 'in_transit'}
              onClick={() => selectStatusFilter('in_transit')}
            />
            <FilterTile
              title="Готов к выдаче"
              value={summary.ready_pickup}
              colorClassName="text-neon-yellow"
              active={statusFilter === 'ready_pickup'}
              onClick={() => selectStatusFilter('ready_pickup')}
            />
            <FilterTile
              title="Оплачено"
              value={summary.paid_ready}
              colorClassName="text-emerald-300"
              active={statusFilter === 'paid_ready'}
              onClick={() => selectStatusFilter('paid_ready')}
            />
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-6 gap-2">
            <FilterTile
              title="Получен"
              value={summary.received}
              colorClassName="text-emerald-300"
              active={false}
              onClick={() => {}}
            />
            <FilterTile
              title="Найдено"
              value={filteredData.length}
              colorClassName="text-text-primary"
              active={false}
              onClick={() => {}}
            />
          </div>
        )}

        {tab === 'current' && statusFilter === 'paid_ready' && filteredData.length > 0 ? (
          <div className="mt-4 rounded-2xl border border-emerald-400/35 bg-emerald-400/10 p-4 text-sm text-emerald-100">
            <div className="font-semibold">Оплаченные заказы готовы к получению</div>
            <div className="mt-1 text-xs text-emerald-100/75">
              Покажите кассиру код выдачи из карточки заказа.
            </div>
          </div>
        ) : null}

        {tab === 'current' && statusFilter === 'ready_pickup' && readyPickupTotals.count > 0 ? (
          <div
            className="mt-4 rounded-2xl border border-neon-yellow/35 bg-gradient-to-br from-neon-yellow/10 via-white/3 to-white/2 p-4 shadow-[0_18px_70px_rgba(0,0,0,0.35)]"
            style={{ boxShadow: '0 0 0 1px rgba(250,204,21,0.15), 0 18px 70px rgba(0,0,0,0.35)' }}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-neon-yellow">Готов к выдаче</div>
              <div className="text-xs text-text-secondary">
                {readyPickupTotals.count} шт. · к оплате {readyPickupTotals.unpaidCount}
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-neon-yellow/20 bg-black/10 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-text-muted">Общий вес</div>
                <div className="mt-0.5 text-base font-semibold text-text-primary">
                  {formatNumber(readyPickupTotals.totalWeight, { decimals: 2 })}
                </div>
              </div>
              <div className="rounded-xl border border-neon-yellow/20 bg-black/10 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-text-muted">Всего к выдаче</div>
                <div className="mt-0.5 text-base font-semibold text-text-primary">
                  {formatNumber(userCountry === 'KZ' ? readyPickupTotals.totalTenge : readyPickupTotals.totalSom, { decimals: 2 })}
                </div>
              </div>
              <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/10 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-emerald-200/80">Уже оплачено</div>
                <div className="mt-0.5 text-base font-semibold text-emerald-100">
                  {formatNumber(userCountry === 'KZ' ? readyPickupTotals.paidTenge : readyPickupTotals.paidSom, { decimals: 2 })}
                </div>
              </div>
              <div className="rounded-xl border border-neon-yellow/20 bg-neon-yellow/10 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-text-muted">
                  {userCountry === 'KZ' ? 'К оплате сейчас (тенге)' : 'К оплате сейчас (сом)'}
                </div>
                <div className="mt-0.5 text-base font-semibold text-text-primary">
                  {formatNumber(userCountry === 'KZ' ? readyPickupTotals.unpaidTenge : readyPickupTotals.unpaidSom, { decimals: 2 })}
                </div>
              </div>
            </div>
            {!onlinePaymentAvailable && !readyPaymentSummary.allPaid ? (
              <div className="mt-3 rounded-2xl border border-stroke bg-white/5 px-3 py-3 text-xs text-text-secondary">
                Онлайн-оплата через банк сейчас доступна только для Кыргызстана. Оплатить заказ можно при получении.
              </div>
            ) : readyPaymentSummary.allPaid ? (
              <div className="mt-3 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 px-3 py-3 text-xs text-emerald-100">
                <div className="font-semibold">Оплачено</div>
                <div className="mt-1">Покажите код выдачи кассиру.</div>
                {visiblePaidOrders.map((order) => (
                  <div key={order.id} className="mt-2 rounded-xl border border-emerald-400/20 bg-black/10 px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-mono text-emerald-50/90">{order.track}</div>
                        <div className="mt-1 text-[10px] uppercase tracking-wide text-emerald-100/70">
                          {formatNumber(userCountry === 'KZ' ? order.tenge : order.som, { decimals: 2 })} {userCountry === 'KZ' ? 'тенге' : 'сом'}
                        </div>
                      </div>
                      <div className="shrink-0 rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-2 py-1 font-mono text-base font-bold text-emerald-50">
                        {order.pickupCode || 'не назначен'}
                      </div>
                    </div>
                  </div>
                ))}
                {hiddenPaidOrdersCount > 0 ? (
                  <button
                    type="button"
                    onClick={() => setPaidOrdersExpanded(true)}
                    className="mt-2 flex h-9 w-full items-center justify-center gap-2 rounded-xl border border-emerald-400/20 bg-black/10 text-xs font-semibold text-emerald-100"
                  >
                    <ChevronDown size={15} />
                    Показать ещё {hiddenPaidOrdersCount}
                  </button>
                ) : paidOrdersExpanded && readyPaymentSummary.paid.length > paidOrdersPreviewLimit ? (
                  <button
                    type="button"
                    onClick={() => setPaidOrdersExpanded(false)}
                    className="mt-2 h-9 w-full rounded-xl border border-emerald-400/20 bg-black/10 text-xs font-semibold text-emerald-100"
                  >
                    Свернуть список
                  </button>
                ) : null}
              </div>
            ) : (!paymentQr?.qr || paymentQrExpired) ? (
              <>
                <button
                  type="button"
                  onClick={createPaymentQr}
                  disabled={paymentLoading}
                  className="mt-3 h-11 w-full rounded-2xl bg-neon-yellow text-sm font-bold text-black disabled:opacity-60"
                >
                  {paymentLoading
                    ? 'Создаём QR...'
                    : `Оплатить ${formatNumber(readyPickupTotals.unpaidSom, { decimals: 2 })} сом`}
                </button>
                <div className="mt-2 text-[11px] text-text-secondary">
                  После нажатия мы сформируем вашу ссылку и QR для оплаты через банк.
                </div>
              </>
            ) : null}
            {paymentError ? <div className="mt-2 text-xs text-red-300">{paymentError}</div> : null}
            {paymentMessage && !paymentQrExpired ? <div className="mt-2 text-xs text-text-secondary">{paymentMessage}</div> : null}
            {onlinePaymentAvailable && paymentQr?.qr && !paymentQrExpired && !readyPaymentSummary.allPaid ? (
              <div className="mt-3 rounded-2xl border border-neon-yellow/20 bg-black/10 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-semibold text-text-primary">Оплата заказа</div>
                  <PaymentStatusBadge status={paymentQr.payment?.status} />
                </div>
                <div className="mt-2 text-xs text-text-secondary">
                  К оплате по этому QR: <span className="font-semibold text-text-primary">{formatNumber(paymentQr.payment?.amount_som, { decimals: 2 })} сом</span>
                </div>
                {paymentQr.overlap ? (
                  <div className="mt-2 rounded-xl border border-amber-300/30 bg-amber-300/10 px-3 py-2 text-[11px] leading-relaxed text-amber-100">
                    Уже создан активный счёт на часть заказов. Новый QR на общую сумму не создаём, чтобы не было двойной оплаты.
                  </div>
                ) : null}
                <div className="mt-2 rounded-xl border border-stroke bg-white/5 px-3 py-2 text-[11px] leading-relaxed text-text-secondary">
                  Ваша ссылка для оплаты готова. После оплаты статус обычно обновится автоматически.
                </div>
                <div className="mt-1 text-[11px] text-text-muted">
                  Transaction ID: <span className="font-mono">{paymentQr.qr.transactionId}</span>
                </div>
                {paymentQr.qr.qrBase64 ? (
                  <img
                    src={`data:image/png;base64,${paymentQr.qr.qrBase64}`}
                    alt="QR для оплаты"
                    className="mt-3 mx-auto h-56 w-56 rounded-2xl bg-white p-2 object-contain"
                  />
                ) : null}
                {paymentQr.qr.qrUrl ? (
                  <a
                    href={paymentQr.qr.qrUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 flex h-10 items-center justify-center gap-2 rounded-2xl border border-neon-yellow/30 bg-neon-yellow/10 text-xs font-semibold text-neon-yellow"
                  >
                    <ExternalLink size={16} />
                    Перейти к оплате в банке
                  </a>
                ) : null}
                <button
                  type="button"
                  onClick={refreshPaymentStatus}
                  disabled={paymentStatusLoading}
                  className="mt-2 flex h-10 w-full items-center justify-center gap-2 rounded-2xl border border-stroke bg-white/5 text-xs font-semibold text-text-primary disabled:opacity-60"
                >
                  <RefreshCw size={16} className={paymentStatusLoading ? 'animate-spin' : ''} />
                  {paymentStatusLoading ? 'Проверяем оплату...' : 'Я оплатил, проверить статус'}
                </button>
                {paymentStatusLabel(paymentQr.payment?.status) === 'Оплачено' ? (
                  <div className="mt-3 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-200">
                    Оплачено. Покажите код выдачи кассиру.
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-5 space-y-3 md:hidden">
          {filteredData.map((o) => (
            <OrderCard key={o.track} order={o} userCountry={userCountry} />
          ))}
        </div>

        <div className="mt-5 rounded-2xl border border-stroke bg-surface backdrop-blur-md overflow-hidden hidden md:block">
          <div>
            <table className="w-full text-left">
              <thead className="bg-white/3">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="border-b border-stroke">
                    {headerGroup.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sortState = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-text-secondary ${
                            canSort ? 'cursor-pointer select-none hover:text-text-primary' : ''
                          }`}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        >
                          <div className="flex items-center gap-2">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sortState === 'asc' ? <span className="text-text-muted">▲</span> : null}
                            {sortState === 'desc' ? <span className="text-text-muted">▼</span> : null}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b border-stroke last:border-b-0 hover:bg-white/2">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-4 text-sm align-middle">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>
  );
};

export default OrdersPage;

